import styled from '@emotion/styled'
import { wrapContained as withStyle, stylesIf, themeColor } from 'utils/styles'
import {
  TYPES_WITHOUT_ORIGIN_PIPE,
  SINGLE_CONDITION_NAMES,
} from 'utils/constants'
import EvaluationBadge from 'components/feature/EvaluationBadge'
import { Icon, DropdownMenu } from 'components/patterns'
import { StyledEdgeImg } from '../ConditionLabel/ConditionLabel.style'
import ConditionLabel from '../ConditionLabel'

const rightBoxRadius = (isTopCondition, isBottomCondition) => `
  border-top-right-radius: ${isTopCondition ? '8px' : '4px'};
  border-bottom-right-radius: ${isBottomCondition ? '8px' : '4px'};
`
const getCarryToLineColor = (isSimulating, carryToEvalutation) => {
  if (!isSimulating) {
    return 'slateSecondary'
  }
  return carryToEvalutation ? 'confirmPrimary' : 'errorPrimary'
}

export default withStyle((props) => {
  const {
    isSelected,
    rungName,
    rungType,
    selectedInputDescription,
    hasErrors,
    isLast,
    isSimulating,
    carryToEvalutation,
  } = props

  const {
    featureSubQueryBackgroundColor,
    rungErrorBackgroundColor,
    rungActiveBackgroundColor,
    rungValidBackgroundColor,
  } = props.theme

  const isDefined = Boolean(selectedInputDescription)
  const definedInput =
    (!TYPES_WITHOUT_ORIGIN_PIPE.has(rungType) && isDefined) ||
    SINGLE_CONDITION_NAMES.includes(rungName)
  const invalidCondition = hasErrors && !isDefined

  const showOriginPipe = invalidCondition || definedInput

  const rungBackgroundColor = () => {
    if (hasErrors) return rungErrorBackgroundColor
    if (isSelected) return rungActiveBackgroundColor
    return rungValidBackgroundColor
  }

  const pipeStyle = `&:before {
    content: '';
    width: 4px;
    top: 0;
    bottom: 0;
    left: 0;
    position: absolute;
    background: ${rungBackgroundColor()};
  `
  return `
    align-items: flex-start;
    justify-content: flex-end;
    height: 30px;
    display: flex;
    align-items: normal;
    width:100%;
    position: relative;
    background: ${featureSubQueryBackgroundColor};
    margin-top: ${isLast ? '-1px' : ''};
    margin-bottom: ${isLast ? '10px' : ''};
    margin-left: -1px;

    .carry-line{
      pointer-events: none;
      position: absolute;
      top:calc(50% - 1.5px);
      height:2px;
      background:${themeColor(
        getCarryToLineColor(isSimulating, carryToEvalutation),
        props
      )};
      &.relation-type-complex,
      &.relation-type-carry-logicset,
      &.relation-type-timer{
        right: 100%;
        width: 30px;
      }
      &.relation-type-simple{
        width: 0;
      }
      &.relation-type-carry-timer,
      &.relation-type-self-reference {
        width: 30px;
        right: 100%;
      }

    }
    .timer-query .carry-line {
      &.relation-type-self-reference,
      &.relation-type-carry-logicset{
        right: 100%;
        width: 30px;
      }
    }

    .relation-type-self .single-query-input-editor,
    .relation-type-carry .single-query-input-editor{
      width:100% !important;
    }
    .rung-tooltip{
      div:not(:last-child){
        margin-bottom: .5em
      }
    }
  
    ${stylesIf(showOriginPipe, pipeStyle)}
  
    // needed to prevent text from moving on hover
    // due to the change in border width
    &:after {
      content: '';
      width: 1px;
    } 
    
  `
})

export const StyledConditionLabel = styled(ConditionLabel)((props) => {
  const { isSelected, isLast, isFirst, hasErrors, isSimulating } = props

  const {
    featureBorderSelectedColor,
    featureSubQueryBackgoundSelectedColor,
    featureBorderHoveredColor,
    featureBorderColor,
    featureBorderWarningColor,
  } = props.theme

  const { whitePrimary } = props.theme.color

  const isBottomCondition = isLast
  const isTopCondition = isFirst

  const hoverStyles = `
    cursor: pointer;
    * { cursor: pointer; }

    &:hover {
      border-color: ${featureBorderHoveredColor};
      border-width: 2px;
      height: 30px;
      &:after {
        content: none;
      }
      ${isSimulating ? Icon : StyledEdgeImg} {
        margin-right: 9px;
      }
    }
    `

  const selectedStyles = `
    background: ${featureSubQueryBackgoundSelectedColor};
    border-color: ${featureBorderSelectedColor};
    border-width: 2px;

    &:after {
      content: none;
    }
  `
  return `
    ${rightBoxRadius(isTopCondition, isBottomCondition)}

      border: 1px solid ${featureBorderColor};
      border-left: none;

      padding-left: 12px;

      .edge-image{
        width: 26px;
        position: relative;
        text-align: center;
        img{
          background-color: ${whitePrimary};
        }
      }
      .rung-readonly{
        display:flex;
        width: 100%;
        .edge-image{
          top:-1px;
        }
        &.vertical {
          display: block;
          padding-left: 0;
        }
      }
      
      ${!isSelected ? hoverStyles : selectedStyles}
      ${stylesIf(
        hasErrors,
        `
        border: 2px solid ${featureBorderWarningColor};
        border-left: none;
      `
      )}
  `
})

export const StyledEvaluationBadge = styled(EvaluationBadge)`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  margin: auto;
  transform: translateX(-50%);
  padding: 0; // ie11
`

export const StyledRelationSymbol = styled.div`
  position: absolute;
  right: 100%;
  display: flex;
  top: 0;
  bottom: 0;
  height: 20px;
  margin: auto;
  .multiple-query-carry {
    margin-left: auto;
    min-width: 34px;
    position: relative;
    display: flex;
    align-items: center;
  }
`

export const StyledRungItem = styled.div((props) => {
  const { isSingleCondition, isLast, isFirst } = props
  const isBottomCondition = isLast
  const isTopCondition = isFirst

  const simpleWidth = `
    width: 323px !important;
  `
  return `
    position: relative;
    ${rightBoxRadius(isTopCondition, isBottomCondition)}
    ${stylesIf(isSingleCondition, simpleWidth)}
  `
})

export const StyledDropdown = styled(DropdownMenu)`
  position: absolute;
  top: calc(50% - 11.5px);
  right: calc(100% + 11.5px);
  cursor: pointer;
`
